import Image from 'next/image';

import { ChevronDown, Quote } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import type { MarketingLocale } from '~/lib/marketing/site';

import { LIGHT_LOGO_FILES } from '../../_lib/home.content';
import {
  TESTIMONIALS,
  TESTIMONIALS_SECTION,
  TESTIMONIAL_WALL,
  type Testimonial,
  type TestimonialId,
} from '../../_lib/testimonials.content';

const LOGO_BASE = '/landing-pages/flow/customers';

/**
 * Testimonial wall. Each card leads with a short verbatim pull quote (the
 * `excerpt`, always an exact span of the client's words) and keeps the full
 * review one click away in a native <details>, so the page stays scannable
 * and the full text is still in the HTML. No Review/AggregateRating schema.
 */
export function TestimonialsSection({
  locale,
  ids = TESTIMONIAL_WALL,
  bg = 'bone',
}: {
  locale: MarketingLocale;
  ids?: TestimonialId[];
  bg?: 'bone' | 'sand';
}) {
  const featureFirst = ids.length > 2;

  return (
    <section
      id="testimonials"
      className={cn(
        'scroll-mt-16 px-4 py-16 md:px-8 md:py-24 lg:px-[90px] lg:py-[120px]',
        bg === 'bone' ? 'bg-[#f7f6f4]' : 'bg-[#ede8e2]',
      )}
    >
      <div className="mx-auto max-w-[1320px]">
        <div className="mb-10 max-w-[760px] md:mb-14">
          <div className="mb-4 flex items-center gap-3 lg:mb-6">
            <span className="h-2 w-2 rounded-full bg-[#202020]" aria-hidden="true" />
            <p className="font-roboto-mono text-[12px] uppercase tracking-wide text-[#202020] md:text-[14px]">
              {TESTIMONIALS_SECTION.label[locale]}
            </p>
          </div>
          <h2
            className="font-roboto text-[30px] leading-[1.12] text-[#202020] md:text-[40px] lg:text-[48px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {TESTIMONIALS_SECTION.heading[locale]}
          </h2>
        </div>

        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2 lg:grid-cols-3">
          {ids.map((id, index) => (
            <TestimonialCard
              key={id}
              testimonial={TESTIMONIALS[id]}
              locale={locale}
              featured={featureFirst && index === 0}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({
  testimonial,
  locale,
  featured,
}: {
  testimonial: Testimonial;
  locale: MarketingLocale;
  featured: boolean;
}) {
  const attribution = [testimonial.person?.[locale], testimonial.role?.[locale]]
    .filter(Boolean)
    .join(locale === 'ar' ? '، ' : ', ');
  const [openQuote, closeQuote] = locale === 'ar' ? ['«', '»'] : ['\u201c', '\u201d'];
  const translatedNote = TESTIMONIALS_SECTION.translatedNote[locale];

  return (
    <figure
      className={cn(
        'flex flex-col rounded-[20px] border p-7 md:p-8',
        featured
          ? 'border-[#202020] bg-[#202020] text-white md:col-span-2'
          : 'border-[#dcd7cf] bg-white text-[#202020]',
      )}
    >
      <div className="mb-6 flex items-center justify-between gap-4">
        <Image
          src={`${LOGO_BASE}/${testimonial.logo}`}
          alt={testimonial.company}
          width={120}
          height={48}
          className={cn(
            'h-12 w-auto max-w-[160px] object-contain object-left rtl:object-right',
            !featured && LIGHT_LOGO_FILES.has(testimonial.logo) && 'brightness-0',
          )}
        />
        <Quote
          aria-hidden="true"
          className={cn('h-6 w-6 shrink-0', featured ? 'text-[#a9c8ff]' : 'text-[#dcd7cf]')}
        />
      </div>

      <blockquote
        className={cn(
          'font-roboto',
          featured
            ? 'text-[22px] text-white md:text-[28px]'
            : 'text-[18px] text-[#202020] md:text-[19px]',
          'leading-snug',
        )}
        style={{ fontVariationSettings: "'wdth' 100" }}
      >
        {openQuote}
        {testimonial.excerpt[locale]}
        {closeQuote}
      </blockquote>

      <figcaption className="mt-6">
        {attribution && (
          <span className="block font-roboto text-[15px] font-medium">{attribution}</span>
        )}
        <span
          className={cn(
            'block font-roboto-mono text-[11px] uppercase tracking-wide',
            featured ? 'text-white/50' : 'text-[#8a857c]',
          )}
        >
          {testimonial.company}
        </span>
      </figcaption>

      <details
        className={cn(
          'group mt-6 border-t pt-4',
          featured ? 'border-white/15' : 'border-[#ebe7e1]',
        )}
      >
        <summary
          className={cn(
            'flex cursor-pointer list-none items-center gap-2 font-roboto-mono text-[11px] uppercase tracking-wide [&::-webkit-details-marker]:hidden',
            featured ? 'text-white/70 hover:text-white' : 'text-[#2b2b2b]/70 hover:text-[#202020]',
          )}
        >
          <span className="group-open:hidden">{TESTIMONIALS_SECTION.readFull[locale]}</span>
          <span className="hidden group-open:inline">{TESTIMONIALS_SECTION.showLess[locale]}</span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
        </summary>
        <div
          className={cn(
            'mt-4 space-y-4 font-roboto text-[15px]',
            featured ? 'text-white/75' : 'text-[#2b2b2b]/80',
            'leading-relaxed',
          )}
        >
          {testimonial.paragraphs[locale].map((paragraph) => (
            <p key={paragraph.slice(0, 40)}>{paragraph}</p>
          ))}
          {translatedNote && (
            <p className={cn('text-[11px]', featured ? 'text-white/40' : 'text-[#8a857c]')}>
              {translatedNote}
            </p>
          )}
        </div>
      </details>
    </figure>
  );
}
